import type { Table } from '@tanstack/react-table';
import { Award, BicepsFlexed, Drama, Frown, ThumbsDown } from 'lucide-react';
import { memo, useCallback, useEffect } from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { cn } from 'lib/utils';

export type DiscountStatusType =
  | 'bad'
  | 'best'
  | 'only'
  | 'accept'
  | 'static'
  | undefined;

type Props = {
  table: Table<IOneItemInPrepareForRequest>;
  discountType: DiscountStatusType;
  setDiscountType: React.Dispatch<React.SetStateAction<DiscountStatusType>>;
};

const discountTypesArray = [
  {
    value: 'bad',
    label: 'سئ',
    bg: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: <ThumbsDown className="h-4 w-4" />,
  },
  {
    value: 'only',
    label: 'الوحيد',
    bg: 'bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/30',
    text: 'text-indigo-700 dark:text-indigo-300',
    border: 'border-indigo-200 dark:border-indigo-800',
    icon: <BicepsFlexed className="h-4 w-4" />,
  },
  {
    value: 'best',
    label: 'جيد جداً',
    bg: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: <Award className="h-4 w-4" />,
  },
  {
    value: 'accept',
    label: 'مقبول',
    bg: 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/30 dark:to-gray-800/30',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-200 dark:border-gray-800',
    icon: <Frown className="h-4 w-4" />,
  },
  {
    value: 'static',
    label: 'ثابت',
    bg: 'bg-gradient-to-br from-fuchsia-100 to-fuchsia-200 dark:from-fuchsia-900/30 dark:to-fuchsia-800/30',
    text: 'text-fuchsia-700 dark:text-fuchsia-300',
    border: 'border-fuchsia-200 dark:border-fuchsia-800',
    icon: <Drama className="h-4 w-4" />,
  },
];

function DiscountStatusFilter({ table, discountType, setDiscountType }: Props) {
  const discountStatusColumn = table.getColumn('DiscountStatus');

  // Ensure the column is available for filtering
  useEffect(() => {
    if (discountStatusColumn && discountType) {
      discountStatusColumn.setFilterValue(discountType);
    }
  }, [discountStatusColumn, discountType]);

  const handleDiscountChange = useCallback(
    (value: DiscountStatusType) => {
      setDiscountType(value);
      if (discountStatusColumn) {
        discountStatusColumn.setFilterValue(value);
      }
    },
    [setDiscountType, discountStatusColumn],
  );

  return (
    <div className="space-y-1 rounded-lg border bg-white/50 p-1.5 shadow-sm backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50">
      <div className="flex items-center justify-between">
        <h3 className="text-2xs font-semibold text-gray-900 dark:text-gray-100">
          حالة السعر
        </h3>
        {discountType && (
          <span className="rounded-full bg-indigo-100 px-1 py-0.5 text-2xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            مفعل
          </span>
        )}
      </div>
      <ToggleGroup
        value={discountType || ''}
        onValueChange={(value) => {
          handleDiscountChange(
            value ? (value as DiscountStatusType) : undefined,
          );
        }}
        type="single"
        className="grid grid-cols-3 gap-1"
      >
        {discountTypesArray.map((item) => (
          <ToggleGroupItem
            key={item.value}
            value={item.value}
            aria-label={item.label}
            className={cn(
              'group relative flex h-6 items-center justify-between overflow-hidden rounded-md border p-0 transition-all duration-200 hover:scale-[1.02] hover:shadow-md',
              item.border,
              item.value === discountType &&
                'border-2 border-primary shadow-sm ring-1 ring-primary/20',
            )}
          >
            <div
              className={cn(
                'flex h-full w-6 items-center justify-center border-l transition-colors duration-200',
                item.bg,
                item.border,
                item.text,
              )}
            >
              {item.icon}
            </div>
            <div className="flex-1 text-center text-[10px] font-medium text-gray-700 transition-colors duration-200 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-gray-100">
              {item.label}
            </div>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

export default memo(DiscountStatusFilter);
